/**
 * STK-352 — recap detail action state classifier.
 *
 * Single source of truth that turns a pre-order + its modify-buffer flags
 * into the small set of orthogonal axes that decide which lifecycle buttons
 * the recap **detail page** shows. Nothing else re-derives a "case"; the
 * button table (`buttonTable.ts`) switches on these axes, and `caseId` is a
 * derived label kept only for tests/telemetry so we can prove every spec
 * case is covered.
 *
 * Pure — no hooks, no store reads. The two booleans that come from the modify
 * buffer (`inModifyMode`, `hasUserMods`) are passed in by the caller. The
 * local optimistic-confirm signal (`confirmInFlightStore`) is layered on top
 * at render time (see `RecapActionBar`), because a pure classifier can only
 * see the server-stamped `export.status`.
 *
 * Why axes and not a flat enum: a failed/in-progress export, an `ill` drift,
 * an EXPORTED lock and a soft-delete are all **orthogonal** to the 10 spec
 * cases (they co-exist with PENDING/CONFIRMED, since failure is metadata-only
 * — `pre_orders.status` never flips to a "failed" value). `exportPhase` is
 * therefore evaluated independently and, in the renderer, takes precedence
 * over the status-based case lookup.
 */
import { type PreOrder, PreOrderStatus } from "@/app/api/types/preOrder";
import { hasAnyPendingAi } from "../services/recapService";
import { isProposalPreOrder } from "../types/proposal.types";

/**
 * Export lifecycle overlay, derived from `pre_order_metadata`. Independent of
 * `PreOrderStatus`. Priority is `failed > ill > confirming > idle`.
 *
 * - `confirming` — `export.status === "in_progress"` (M3 round-trip running).
 * - `failed`     — `export.status === "failed"` (M3 reject / timeout / drift).
 *                  The order stays at its pre-confirm `status` (usually
 *                  PENDING) — confirm never optimistically flips status.
 * - `ill`        — `sync_health.ill === true` (post-export drift; heal/retry).
 */
export type ExportPhase = "idle" | "confirming" | "failed" | "ill";

/**
 * Discriminates the lifecycle shape so the renderer can short-circuit the
 * 10-case lookup for states the spec doesn't enumerate.
 *
 * - `live`     — PENDING / CONFIRMED / AI_PROPOSAL: the 10 spec cases apply.
 * - `exported` — status EXPORTED (2): locked, read-only green pill.
 * - `cancelled`— CANCELLED (6) or soft-deleted: the focus bar offers Restore.
 * - `proposal` — legacy virtual `proposal-<batchId>` PO (no modify buffer):
 *                must NOT reach the generic per-order path; resolved through
 *                the proposal-batch surface instead.
 */
export type RecapLifecycle = "live" | "exported" | "cancelled" | "proposal";

/**
 * Derived spec-case label (telemetry/tests only). `1`..`10` map to the issue's
 * cases; the modify-mode variants (1'/5'/8') collapse onto their base modify
 * case (2/6/10) because the button SET is identical — only `hasUserMods`
 * toggles the main button's enabled state and the delete-warning, both handled
 * downstream. The string ids cover the non-`live` lifecycles.
 */
export type RecapCaseId =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | "exported"
  | "cancelled"
  | "proposal";

export interface RecapActionState {
  lifecycle: RecapLifecycle;
  status: PreOrderStatus;
  /** Header or any non-deleted flow carries a pending `ai_suggestions[]`. */
  hasPendingAi: boolean;
  /** Modify buffer is open (`buffer.isModifying`). */
  inModifyMode: boolean;
  /** Buffer holds at least one edit/add/delete (`pendingInfo.total > 0`). */
  hasUserMods: boolean;
  isDeleted: boolean;
  exportPhase: ExportPhase;
  caseId: RecapCaseId;
}

export interface ClassifyInput {
  preOrder: PreOrder;
  inModifyMode: boolean;
  hasUserMods: boolean;
}

/**
 * Export overlay from metadata. Pure — server-stamped status only.
 *
 * Priority: failed > confirming (in_progress) > ill > idle. `failed` and
 * `in_progress` are mutually exclusive values of the same `export.status`
 * field, so the only real contest is `in_progress` vs the separate
 * `sync_health.ill` flag — and an in-flight export ranks higher because
 * re-exporting is precisely how an `ill` (ERP-drift) order is healed: while
 * that export runs we surface the live "Confirming…" state, and `ill`
 * re-surfaces afterward if the drift persists.
 */
export function deriveExportPhase(preOrder: PreOrder): ExportPhase {
  const exp = preOrder.pre_order_metadata?.export?.status ?? null;
  if (exp === "failed") return "failed";
  if (exp === "in_progress") return "confirming";
  if (preOrder.pre_order_metadata?.sync_health?.ill === true) return "ill";
  return "idle";
}

function deriveLifecycle(preOrder: PreOrder): RecapLifecycle {
  // Legacy proposal-batch virtual PO — synthesized id, no real status/buffer.
  if (isProposalPreOrder(preOrder)) return "proposal";
  if (preOrder.deleted_at != null) return "cancelled";
  // Strictly the PO-level CANCELLED status — NOT `isCancelledPreOrder`, which
  // also returns true when any single flow line is cancelled (status 3). A
  // live PENDING order with one cancelled line is still live: classifying it
  // "cancelled" would force a Restore-only bar and hide confirm / delete.
  if (preOrder.status === PreOrderStatus.CANCELLED) return "cancelled";
  if (preOrder.status === PreOrderStatus.EXPORTED) return "exported";
  return "live";
}

/**
 * Map the `live` axes to the spec case number. Only meaningful for the
 * `live` lifecycle with `exportPhase === "idle"` — failed/ill/confirming
 * overlays are decided independently in the renderer.
 */
function deriveCaseId(
  status: PreOrderStatus,
  hasPendingAi: boolean,
  inModifyMode: boolean,
): RecapCaseId {
  if (status === PreOrderStatus.AI_PROPOSAL) return inModifyMode ? 2 : 1;
  if (status === PreOrderStatus.PENDING) {
    if (hasPendingAi) return inModifyMode ? 6 : 5;
    return inModifyMode ? 4 : 3;
  }
  // CONFIRMED
  if (hasPendingAi) return inModifyMode ? 10 : 8;
  return inModifyMode ? 9 : 7;
}

/**
 * Classify a pre-order into the action-state axes. Pure; safe to memoize on
 * `(preOrder, inModifyMode, hasUserMods)`.
 */
export function classifyRecapState({
  preOrder,
  inModifyMode,
  hasUserMods,
}: ClassifyInput): RecapActionState {
  const lifecycle = deriveLifecycle(preOrder);
  const status = preOrder.status as PreOrderStatus;
  const hasPendingAi = hasAnyPendingAi(preOrder);
  const isDeleted = preOrder.deleted_at != null;
  const exportPhase = deriveExportPhase(preOrder);

  let caseId: RecapCaseId;
  if (lifecycle === "exported") caseId = "exported";
  else if (lifecycle === "cancelled") caseId = "cancelled";
  else if (lifecycle === "proposal") caseId = "proposal";
  else caseId = deriveCaseId(status, hasPendingAi, inModifyMode);

  return {
    lifecycle,
    status,
    hasPendingAi,
    inModifyMode,
    hasUserMods,
    isDeleted,
    exportPhase,
    caseId,
  };
}
